// Container for the unified ShareDialog (Doc 08 §8): builds the shareStore for a
// resource (trip or template) and binds its state/actions to the presentational
// dialog. Pages render this with the resource's members map; everything else —
// classification, cap enforcement, invites, links — lives in the viewmodel store.

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { DomainError, ManagedProfileId, ParticipantId, Result, UserId } from "@pack-it-up/shared";
import { Toast, vars } from "@pack-it-up/ui-web";
import type {
  AllRepositories,
  MemberEntry,
  ParticipantRemovalPlan,
  ParticipantRow,
  ResourceRef,
  Role,
} from "@pack-it-up/core";
import { createShareStore } from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { useSessionDeps } from "../../app/sessionDeps.js";
import { RemoveParticipantDialog } from "../participants/RemoveParticipantDialog.js";
import { ShareDialog } from "./ShareDialog.js";

// How long the post-confirm Undo toast stays actionable before the removal executes —
// the same window the template editor's delete uses.
const UNDO_WINDOW_MS = 6000;

export interface ShareDialogHostProps {
  open: boolean;
  onClose: () => void;
  resource: ResourceRef;
  resourceName: string;
  members: Record<string, MemberEntry>;
  // Owner/editor only — gates the store's shareLinks/pendingInvites subscriptions,
  // which rules deny to a viewer. A viewer still opens this dialog read-only.
  canManage: boolean;
  // Issue #188 — a template's travelers section, rendered inside the dialog under the
  // members. Passed through untouched; the template editor owns the control.
  travelers?: ReactNode;
  // Issue #185 — the resource's rows, so removing a member can clean up the ones they
  // were the sole allocatee of. Trip-only: a template's members are collaborators, not
  // travelers, so removing one is never assigned anything. Omitting it keeps the old
  // one-click removal.
  rows?: () => ParticipantRow[];
}

export const ShareDialogHost = ({ open, onClose, resource, resourceName, members, canManage, travelers, rows }: ShareDialogHostProps): ReactNode => {
  const { auth } = useStores();
  const { repos } = useSessionDeps();
  const { friends: friendsStore, managed: managedStore } = useSession();
  const actorId = useStoreValue(auth, (s) => s.user?.id ?? null);

  // The store reads friends/managed/memberCount lazily (functions), so it always
  // sees current values; a ref keeps `memberCount` live without rebuilding the store
  // when the members map changes.
  const membersRef = useRef(members);
  useEffect(() => {
    membersRef.current = members;
  }, [members]);

  // Read lazily for the same reason as `members`: the rows change constantly and must
  // not rebuild the store.
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const store = useMemo(
    () => {
      // The row-cleanup applier is an optional seam on the repository bundle, probed the
      // same way the import and template-delete appliers are.
      const seam = repos as AllRepositories & {
        applyParticipantRemovalPlan?: (plan: ParticipantRemovalPlan) => Promise<Result<void, DomainError>>;
      };
      return createShareStore({
        sharing: repos.sharing,
        resource,
        canManage,
        friends: () => friendsStore.getState().friends,
        managed: () => managedStore.getState().profiles,
        memberCount: () => Object.keys(membersRef.current).length,
        rows: () => rowsRef.current?.() ?? [],
        ...(seam.applyParticipantRemovalPlan !== undefined
          ? { applyParticipantRemovalPlan: seam.applyParticipantRemovalPlan }
          : {}),
      });
    },
    // Rebuild only when the resource itself changes (id carries kind's identity) or
    // canManage flips (a role change should start/stop the gated streams).
    [repos, resource.kind, resource.id, canManage, friendsStore, managedStore],
  );

  // The store owns the pending-invite and invite-link streams; a superseded one (the
  // dialog moved to another resource) must unsubscribe or its listeners keep firing into
  // state nothing renders. Deliberately not an effect *cleanup*: StrictMode's simulated
  // remount runs cleanups without rebuilding the memo, which would leave the live store
  // disposed. Disposing only the previous one is remount-safe.
  const previousStore = useRef<typeof store | null>(null);
  useEffect(() => {
    const superseded = previousStore.current;
    previousStore.current = store;
    if (superseded !== null && superseded !== store) superseded.getState().dispose();
  }, [store]);

  const pendingInvites = useStoreValue(store, (s) => s.pendingInvites);
  const links = useStoreValue(store, (s) => s.links);
  const capPrompt = useStoreValue(store, (s) => s.capPrompt);

  // Issue #185 — removal is two steps: a confirm dialog stating what it costs in rows,
  // then a deferred execution behind an Undo toast. Nothing is written until the window
  // elapses, so Undo simply disarms the timer — access and rows are still intact.
  const [removeTarget, setRemoveTarget] = useState<{ id: ParticipantId; name: string } | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{ id: ParticipantId; name: string } | null>(null);
  const removeTimer = useRef<{ timer: ReturnType<typeof setTimeout>; run: () => void } | null>(null);

  // Only one removal can be pending: arming a second flushes the first immediately,
  // since the new toast replaces the Undo affordance that was the first one's point.
  const flushPendingRemoval = (): void => {
    const pending = removeTimer.current;
    if (pending === null) return;
    clearTimeout(pending.timer);
    removeTimer.current = null;
    pending.run();
  };

  const armRemoval = (target: { id: ParticipantId; name: string }): void => {
    setRemoveTarget(null);
    flushPendingRemoval();
    setPendingRemoval(target);
    // The store reads `rows` when the removal *runs*, so the plan is always built from
    // the current list rather than the one shown when it was armed.
    const run = (): void => void store.getState().removeMember(target.id as UserId);
    const timer = setTimeout(() => {
      removeTimer.current = null;
      setPendingRemoval(null);
      run();
    }, UNDO_WINDOW_MS);
    removeTimer.current = { timer, run };
  };

  const undoRemoval = (): void => {
    if (removeTimer.current !== null) clearTimeout(removeTimer.current.timer);
    removeTimer.current = null;
    setPendingRemoval(null);
  };

  if (actorId === null) return null;

  return (
    <>
    <ShareDialog
      open={open}
      onClose={onClose}
      resourceName={resourceName}
      members={members}
      {...(travelers !== undefined ? { travelers } : {})}
      actorId={actorId}
      canManage={canManage}
      pendingInvites={pendingInvites}
      links={links}
      capPrompt={capPrompt}
      classify={(text) => store.getState().classify(text)}
      onShareWithUser={async (userId, role) => {
        await store.getState().shareWithUser(userId as UserId, role);
      }}
      onInviteByEmail={async (email, role) => {
        await store.getState().inviteByEmail(email, role);
      }}
      onRevokePendingInvite={async (inviteId) => {
        await store.getState().revokePendingInvite(inviteId);
      }}
      onAddManagedMember={async (profileId) => {
        await store.getState().addManagedMember(profileId as ManagedProfileId);
      }}
      onChangeRole={async (userId, role: Role) => {
        await store.getState().changeRole(userId as UserId, role);
      }}
      onRemoveMember={async (participantId) => {
        // Without rows (a template's members) there is nothing to clean up, so the old
        // one-click removal stands.
        if (rows === undefined) {
          await store.getState().removeMember(participantId as UserId);
          return;
        }
        setRemoveTarget({
          id: participantId as ParticipantId,
          name: members[participantId]?.profile.displayName ?? "this person",
        });
      }}
      onRenameGuest={async (userId, displayName) => {
        const r = await store.getState().renameGuest(userId as UserId, displayName);
        // A guest correcting its OWN name also updates the auth profile, so the next
        // link it opens prefills the corrected name rather than the typo (T31).
        if (r.ok && userId === actorId) await auth.getState().setAuthDisplayName(displayName);
      }}
      onCreateLink={async (role, opts) => {
        await store.getState().createLink(role, opts);
      }}
      onRevokeLink={async (token) => {
        await store.getState().revokeLink(token);
      }}
    />

    {removeTarget !== null && (
      <RemoveParticipantDialog
        displayName={removeTarget.name}
        resourceKind={resource.kind === "trip" ? "trip" : "template"}
        preview={store.getState().previewRemoveMember(removeTarget.id)}
        onConfirm={() => armRemoval(removeTarget)}
        onClose={() => setRemoveTarget(null)}
      />
    )}

    {pendingRemoval !== null && (
      <div style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
        <Toast message={`Removing ${pendingRemoval.name}…`} onUndo={undoRemoval} />
      </div>
    )}
    </>
  );
};
